import React, { useState } from 'react'
import { CircularProgress, Snackbar, Alert } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  selectDocumentError,
  selectDocumentLoading,
} from '../../redux/documents/documentSelector'
import SuspenseLoader from '../common/SuspenseLoader'
import { downloadFile } from '../../utils/downloadUtils'
import { DetailButton } from '../common/detail'
import '../common/detail/detail.css'

interface DocumentViewProps {
  document: any
  compact?: boolean
  onClose?: () => void
}

const DocumentView: React.FC<DocumentViewProps> = ({ document, onClose }) => {
  const { t } = useTranslation()
  const loading = useSelector(selectDocumentLoading)
  const error = useSelector(selectDocumentError)
  const [pdfError, setPdfError] = useState(false)
  const [downloadState, setDownloadState] = useState<{
    loading: boolean
    error: string | null
  }>({ loading: false, error: null })

  const handleDownload = () => {
    if (!document) return
    setDownloadState({ loading: true, error: null })
    try {
      downloadFile(document.path, document.originalName)
    } catch {
      setDownloadState({
        loading: false,
        error: t('document.downloadError') || 'Download failed',
      })
    } finally {
      setDownloadState((prev) => ({ ...prev, loading: false }))
    }
  }

  if (loading) return <SuspenseLoader />
  if (!document || error) {
    return (
      <div className="doc-viewer-shell">
        <div className="doc-viewer-toolbar">
          <div className="doc-viewer-file">
            <span className="doc-viewer-file-ico" aria-hidden>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 22 }} />
            </span>
            <div>
              <p className="doc-viewer-file-name">{t('document.notFound')}</p>
            </div>
          </div>
          {onClose ? (
            <button
              type="button"
              className="incyia-modal-x"
              onClick={onClose}
              aria-label="Close"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}
        </div>
        <div className="doc-viewer-fallback">
          <p>{t('document.notFound')}</p>
        </div>
      </div>
    )
  }

  const isPdf = document.mimeType === 'application/pdf'
  const pdfViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(
    document.path
  )}&embedded=true`

  return (
    <div className="doc-viewer-shell">
      <Snackbar
        open={!!downloadState.error}
        autoHideDuration={6000}
        onClose={() => setDownloadState((p) => ({ ...p, error: null }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setDownloadState((p) => ({ ...p, error: null }))}
          severity="error"
        >
          {downloadState.error}
        </Alert>
      </Snackbar>

      <div className="doc-viewer-toolbar">
        <div className="doc-viewer-file">
          <span className="doc-viewer-file-ico" aria-hidden>
            {isPdf ? (
              <PictureAsPdfOutlinedIcon sx={{ fontSize: 22 }} />
            ) : (
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 22 }} />
            )}
          </span>
          <div>
            <p className="doc-viewer-file-name">{document.originalName}</p>
            <p className="doc-viewer-file-sub">
              {isPdf
                ? 'PDF'
                : document.mimeType ||
                  t('document.file', { defaultValue: 'File' })}
            </p>
          </div>
        </div>
        <div className="doc-viewer-actions">
          {document.path ? (
            <DetailButton
              variant="ghost"
              size="sm"
              onClick={() =>
                window.open(document.path, '_blank', 'noopener,noreferrer')
              }
            >
              <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
              {t('document.openPdf', { defaultValue: 'Open' })}
            </DetailButton>
          ) : null}
          <DetailButton
            size="sm"
            onClick={handleDownload}
            disabled={downloadState.loading}
          >
            {downloadState.loading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
            )}
            {t('document.downloadFile', { defaultValue: 'Download' })}
          </DetailButton>
          {onClose ? (
            <button
              type="button"
              className="incyia-modal-x"
              onClick={onClose}
              aria-label="Close"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="doc-viewer-frame-wrap">
        {isPdf ? (
          pdfError ? (
            <div className="doc-viewer-fallback">
              <p>{t('document.pdfPreviewError')}</p>
              <DetailButton
                onClick={() =>
                  window.open(document.path, '_blank', 'noopener,noreferrer')
                }
              >
                <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                {t('document.openPdf')}
              </DetailButton>
            </div>
          ) : (
            <iframe
              src={pdfViewerUrl}
              title={document.originalName}
              onError={() => setPdfError(true)}
            />
          )
        ) : (
          <div className="doc-viewer-fallback">
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 40, color: '#8a9ba7' }} />
            <p>{t('document.previewNotAvailable')}</p>
            <DetailButton onClick={handleDownload} disabled={downloadState.loading}>
              <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
              {t('document.downloadFile')}
            </DetailButton>
          </div>
        )}
      </div>
    </div>
  )
}

export default DocumentView
